import { type FormEvent, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirebase } from "../firebase/init.js";
import { applyWriteMap } from "../firebase/writes.js";
import {
  type EditableScene,
  adjustCountdown,
  removeCountdown,
  setCountdown,
} from "../lib/scene-edits.js";

interface Props {
  scenePath: string;
  scene: EditableScene;
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export default function SceneCountdownsEditor({ scenePath, scene }: Props): ReactElement {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [max, setMax] = useState("4");
  const [pub, setPub] = useState(true);

  async function onAdjust(id: string, delta: number): Promise<void> {
    const { db } = getFirebase();
    await applyWriteMap(db, adjustCountdown(scenePath, scene, id, delta));
  }

  async function onRemove(id: string): Promise<void> {
    if (!window.confirm(t("edit.confirm.delete-countdown"))) return;
    const { db } = getFirebase();
    await applyWriteMap(db, removeCountdown(scenePath, id));
  }

  async function onAdd(e: FormEvent): Promise<void> {
    e.preventDefault();
    const id = slug(name) || `cd-${Date.now().toString(36)}`;
    const m = Number.parseInt(max, 10);
    if (!name.trim() || Number.isNaN(m) || m < 1) return;
    const { db } = getFirebase();
    await applyWriteMap(
      db,
      setCountdown(scenePath, id, {
        nameKey: name.trim(),
        max: m,
        current: 0,
        public: pub,
      }),
    );
    setName("");
    setMax("4");
  }

  const countdowns = Object.entries(scene.countdowns ?? {});

  return (
    <section>
      <h3 className="text-sm font-semibold uppercase text-muted-foreground">
        {t("edit.scene.countdowns")}
      </h3>
      <ul className="mt-2 space-y-1 text-sm">
        {countdowns.map(([id, cd]) => (
          <li key={id} className="flex items-center gap-2">
            <span className="flex-1">{cd.nameKey}</span>
            <button
              type="button"
              aria-label={`${t("edit.counter.decrement")} ${cd.nameKey}`}
              className="rounded border px-1"
              onClick={() => onAdjust(id, -1)}
            >
              −
            </button>
            <span className="font-mono">
              {cd.current}/{cd.max}
            </span>
            <button
              type="button"
              aria-label={`${t("edit.counter.increment")} ${cd.nameKey}`}
              className="rounded border px-1"
              onClick={() => onAdjust(id, +1)}
            >
              +
            </button>
            <button
              type="button"
              aria-label={`${t("edit.delete")} ${cd.nameKey}`}
              className="rounded border border-rose-400 px-1 text-rose-700"
              onClick={() => onRemove(id)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={onAdd} className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <input
          className="flex-1 rounded border px-2 py-1"
          placeholder={t("edit.scene.countdown-name-placeholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={t("edit.scene.countdown-name")}
        />
        <input
          type="number"
          min={1}
          max={20}
          className="w-16 rounded border px-2 py-1 text-center"
          value={max}
          onChange={(e) => setMax(e.target.value)}
          aria-label={t("edit.scene.countdown-max")}
        />
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} />
          {t("edit.scene.countdown-public")}
        </label>
        <button type="submit" className="rounded border px-2 py-1">
          {t("edit.scene.add-countdown")}
        </button>
      </form>
    </section>
  );
}
